import React from "react";
import { AuthenticatedTemplate } from "@azure/msal-react";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import RestartScrollContainer from "../containers/RestartScrollContainer";
import ContextProvider from "../context/ContextProvider";
import { getRoleValue, Roles } from "../model/Enums/RolesEnum";
import PageLayout from "../containers/PageLayout";
import AcademicActivityPlan from "../pages/AcademicActivityPlan/AcademicActivityPlan";
import ActivityAssociation from "../pages/ActivityAssociation";
import Calendar from "../pages/Calendar/CalendarView";
import CheckinParameters from "../pages/CheckinParameters/CheckinParameters";
import CoordinatingAssociation from "../pages/CoordinatingAssociation";
import Home from "../pages/Home";
import AcceptanceTermsEditor from "../pages/AcceptanceTermsEditor";
import ImportHistory from "../pages/ImportHistory";
import ManagePermission from "../pages/ManagePermission";
import ParametrizeActivityPlan from "../pages/ParametrizeActivityPlan/ParametrizeActivityPlan";
import Schedule from "../pages/Schedule/ScheduleView";
import TaskManagement from "../pages/TaskManagement/TaskManagement";
import UploadFile from "../pages/UploadFile";
import UserManagement from "../pages/UserManagement";
import GenericUtils from "../utils/GenericUtils";
import gestaoAbono from "../pages/GestaoAbono/GestaoAbono";
import workflowAbono from "../pages/GestaoFaltas/GestaoFaltas";
import Workflows from "../pages/Workflows/Workflows";
import relatorios from "../pages/Summaries/Reports";
import App from "./App";
import Help from "../pages/Help/Help";
import AdministratorTach from "../pages/AdministratorTach/AdministratorTach";
import HistoryTach from "../pages/HistoryTach/HistoryTach";
import PoliticaPrivacidadeMobile from "../pages/PoliticaPrivacidade/PoliticaPrivacidadeMobile";
import Loading from "./Loading";
import MeuPainel from "../pages/MeuPainel/MeuPainel";

import { UserRole } from "../model/Enums/UserRoleEnum";

export interface PrivateRouteProps {
    path?: string;
    requiredRole: Roles;
    component: any;
    redirect?: any;
}

function PrivateRoute({
    path,
    component,
    requiredRole,
    redirect,
}: PrivateRouteProps) {
    const acessTokenTemplateManager =
        GenericUtils.getAccessTokenTemplateManager();
    const acessTokenDocente = GenericUtils.getAccessTokenDocente();
    const accessTokenDocenteReport = GenericUtils.getAccessTokenDocenteReport();
    const user = GenericUtils.getUser();

    if (
        !acessTokenTemplateManager ||
        !acessTokenDocente ||
        !accessTokenDocenteReport
    ) {
        return <Redirect to="/" />;
    }

    const findRole = user?.listaFuncionalidades?.find(
        (item: any) => item?.mnemonico === requiredRole
    );

    if (redirect === null && !user) {
        return <Redirect to="/" />;
    }

    if (!user?.ativo) {
        return <Redirect to="/" />;
    }

    if (getRoleValue(requiredRole) !== getRoleValue(findRole?.mnemonico)) {
        return <Redirect to="/" />;
    }

    return <Route path={path} component={component} />;
}

function getAcessoMeuPainel() {
    let hasAccess = false;

    const user = GenericUtils.getUser();
    hasAccess = user?.listaFuncionalidades?.find(
        (item: any) => item?.idFuncionalidade === UserRole.MeuPainelDocente
    );

    return !!hasAccess;
}

function Router(): JSX.Element {
    return (
        <BrowserRouter>
            <Switch>
                <Route
                    path="/aviso-de-privacidade"
                    component={PoliticaPrivacidadeMobile}
                />
                <ContextProvider>
                    <PageLayout>
                        <AuthenticatedTemplate>
                            <App>
                                <RestartScrollContainer>
                                    <Switch>
                                        <PrivateRoute
                                            path="/importar"
                                            component={UploadFile}
                                            requiredRole={Roles.IMPORTACAO}
                                        />
                                        <PrivateRoute
                                            path="/historico"
                                            component={ImportHistory}
                                            requiredRole={
                                                Roles.HISTORICO_IMPORTACAO
                                            }
                                        />
                                        <PrivateRoute
                                            path="/parametrizar-tach"
                                            component={ParametrizeActivityPlan}
                                            requiredRole={
                                                Roles.PARAMETRIZACAO_PLANO_ATIVIDADE
                                            }
                                        />
                                        <PrivateRoute
                                            path="/associacao-atividade"
                                            component={ActivityAssociation}
                                            requiredRole={
                                                Roles.ASSOCIACAO_ATIVIDADE_PROFESSOR
                                            }
                                        />
                                        <PrivateRoute
                                            path="/calendario"
                                            component={Calendar}
                                            requiredRole={
                                                Roles.GESTAO_CALENDARIO
                                            }
                                        />
                                        <PrivateRoute
                                            path="/associacao-coordenador"
                                            component={CoordinatingAssociation}
                                            requiredRole={
                                                Roles.ASSOCIACAO_PROFESSOR_COORDENADOR
                                            }
                                        />
                                        <PrivateRoute
                                            path="/meu-tach"
                                            component={AcademicActivityPlan}
                                            requiredRole={Roles.PLANO_ATIVIDADE}
                                        />
                                        <PrivateRoute
                                            path="/consultar-tach"
                                            component={AcademicActivityPlan}
                                            requiredRole={
                                                Roles.VISUALIZAR_PLANO_ATIVIDADE
                                            }
                                        />
                                        <PrivateRoute
                                            path="/editar-tach"
                                            component={AcademicActivityPlan}
                                            requiredRole={
                                                Roles.EDITAR_PLANO_ATIVIDADE_DOCENTE
                                            }
                                        />
                                        <PrivateRoute
                                            path="/gerenciar-permissao"
                                            component={ManagePermission}
                                            requiredRole={
                                                Roles.GESTAO_PERMISSAO_PLANO_ATIVIDADE
                                            }
                                        />
                                        <PrivateRoute
                                            path="/calendario-agenda-docente"
                                            component={Calendar}
                                            requiredRole={
                                                Roles.GESTAO_CALENDARIO
                                            }
                                        />
                                        <PrivateRoute
                                            path="/gestao-tarefas"
                                            component={TaskManagement}
                                            requiredRole={Roles.WORKFLOW}
                                        />
                                        <PrivateRoute
                                            path="/gestao-usuarios"
                                            component={UserManagement}
                                            requiredRole={Roles.GESTAO_USUARIO}
                                        />
                                        <PrivateRoute
                                            path="/minha-agenda"
                                            component={Schedule}
                                            requiredRole={Roles.AGENDA_DOCENTE}
                                        />
                                        <PrivateRoute
                                            path="/consultar-agenda"
                                            component={Schedule}
                                            requiredRole={
                                                Roles.VISUALIZAR_AGENDA_DOCENTE
                                            }
                                        />
                                        <PrivateRoute
                                            path="/editar-agenda"
                                            component={Schedule}
                                            requiredRole={
                                                Roles.EDITAR_AGENDA_DOCENTE
                                            }
                                        />
                                        <PrivateRoute
                                            path="/chekin-parametros"
                                            component={CheckinParameters}
                                            requiredRole={
                                                Roles.PARAMETRIZACAO_CHECKIN
                                            }
                                        />
                                        <PrivateRoute
                                            path="/gerenciar-abono"
                                            component={gestaoAbono}
                                            requiredRole={
                                                Roles.GESTAO_ABONO_LEGAL
                                            }
                                        />
                                        <PrivateRoute
                                            path="/workflow-abono"
                                            component={workflowAbono}
                                            requiredRole={Roles.GESTAO_WORKFLOW}
                                        />
                                        <PrivateRoute
                                            path="/workflows"
                                            component={Workflows}
                                            requiredRole={Roles.GESTAO_WORKFLOW}
                                        />
                                        <PrivateRoute
                                            path="/relatorios"
                                            component={relatorios}
                                            requiredRole={Roles.RELATORIOS}
                                        />
                                        <PrivateRoute
                                            path="/termo-de-aceite"
                                            component={AcceptanceTermsEditor}
                                            requiredRole={Roles.TERMO_ACEITE}
                                        />
                                        <PrivateRoute
                                            path="/help"
                                            component={Help}
                                            requiredRole={Roles.HELP}
                                        />
                                        <PrivateRoute
                                            path="/administrador-tach"
                                            component={AdministratorTach}
                                            requiredRole={
                                                Roles.ADMINISTRADOR_TACH
                                            }
                                        />
                                        <PrivateRoute
                                            path="/historico-tach"
                                            component={HistoryTach}
                                            requiredRole={Roles.HISTORICO_TACH}
                                        />
                                        <PrivateRoute
                                            path="/meu-painel"
                                            component={MeuPainel}
                                            requiredRole={
                                                Roles.MEU_PAINEL_DOCENTE
                                            }
                                        />

                                        <Route
                                            exact
                                            path="/"
                                            render={() => {
                                                if (getAcessoMeuPainel()) {
                                                    return <MeuPainel />;
                                                }
                                                return <Home />;
                                            }}
                                        />
                                        <Route path="*">
                                            <Redirect to="/" />
                                        </Route>
                                    </Switch>
                                </RestartScrollContainer>
                                <Loading />
                            </App>
                        </AuthenticatedTemplate>
                    </PageLayout>
                </ContextProvider>
            </Switch>
        </BrowserRouter>
    );
}

export default Router;
